import React, { useState, useMemo, useContext, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import classNames from "classnames/bind";
import { nav } from "./nav";
import {
  ZoomIcon,
  SmallZoomIcon,
} from "../../../components/Icons/Icons";
import { NotificationIcon } from "../../../components/Icons/SideBar/NotificationIcon";
import styles from "./Sidebar.module.css";
import { Menu, Layout, Tooltip, Badge } from "antd";
import { useTranslation } from "react-i18next";
import { StateContext } from "../../Layout/Layout";
import { useStoreContext } from "../../../hooks/useStoreContext";
import { actions } from "../../../store";
import axiosClient_v2 from "../../../services/axiosClient_v2";
import { formatTimeHHMMSS, listAiTranslate } from "../../../utils";

const cx = classNames.bind(styles);

const Sidebar = () => {
  const { collapsed, setCollapsed } = useContext(StateContext);
  const { Sider } = Layout;
  const [current, setCurrent] = useState("");
  const { t } = useTranslation();
  const [dataStore, dispatch] = useStoreContext();
  const navigate = useNavigate();

  const [alertList, setAlertList] = useState([]);
  const [selectedAlertId, setSelectedAlertId] = useState(null);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axiosClient_v2.get("/notification/get-list-notification-popup/", {
        params: {
          system: 0,
          size: 50,
        },
      });

      if (res && res.data && Array.isArray(res.data)) {
        const mapped = res.data.map((item, index) => {
          const rawName = item?.name || item?.NAME || item?.title || item?.TITLE || "";
          const translatedTitle =
            listAiTranslate?.find((ai) => ai.ai_name === rawName)?.name_tr ||
            rawName ||
            "Phát hiện chuyển động";
          const cameraCode =
            item.camera_code || item.codeCamera || item.CAMERA_CODE || "-";
          const cameraName =
            item.name_cam || item.camera_name || item.nameCamera || "-";
          const rawContent = item.detail || item.DETAIL || `${cameraName}`;
          let rawTime = item.time || item.TIME || item.time_occurrence || item.created_at || "";
          const matchTime = rawContent?.match(/vào hồi\s+([0-9: -]+)/i);
          if (matchTime && matchTime[1]) {
            rawTime = matchTime[1].trim();
          }

          const uniqueKey = `${item.time || rawTime}_${cameraCode}_${rawName}_${rawContent}`;

          return {
            id: `alert_${uniqueKey}`,
            uniqueKey: uniqueKey,
            originalItem: item,
            time_occurrence: rawTime,
            time_display: formatTimeHHMMSS(item.time) || rawTime || "00:00:00",
            camera_code: cameraCode,
            camera_name: cameraName,
            title: translatedTitle,
            content: rawContent,
            service_type: item.type || item.service_type || "AI",
            path: item.path || "",
            seen: item.seen,
          };
        });

        // Cứ có dữ liệu mới thì add vào giống như chuông báo, không reset
        setAlertList((prev) => {
          if (!prev || prev.length === 0) {
            return mapped;
          }
          const existingKeys = new Set(prev.map((p) => p.uniqueKey));
          const newItems = mapped.filter((m) => !existingKeys.has(m.uniqueKey));
          if (newItems.length > 0) {
            return [...newItems, ...prev];
          }
          return prev;
        });
      }
    } catch (err) {
      console.error("Sidebar fetch alerts error:", err);
    }
  }, []);

  // Tải dữ liệu ban đầu & khi toggleNotification thay đổi
  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts, dataStore?.toggleNotification]);

  // Realtime tự động cập nhật danh sách cảnh báo ở menu mỗi 4 giây
  useEffect(() => {
    const timer = setInterval(() => {
      fetchAlerts();
    }, 4000);
    return () => clearInterval(timer);
  }, [fetchAlerts]);

  const handleSelectAlert = (alertItem) => {
    setSelectedAlertId(alertItem.id);
    dispatch(actions.setSelectedAiAlert(alertItem));
    navigate("/ai-alert");
  };

  const getSideBar = (nav, collapsed) => {
    const listNav = nav?.map((item, index) => {
      return {
        key: `${item?.title}_${index}`,
        icon: item?.icon,
        label: (
          <Link
            to={item?.to}
            target={item?.target}
            style={{
              paddingLeft: 8,
              fontSize: 14,
              paddingRight: 4,
              color: "rgba(0, 0, 0, 1)",
            }}
          >
            {!collapsed && item?.title}
          </Link>
        ),
      };
    });

    // Cảnh báo AI là một menu item trong Menu để căn lề thẳng hàng 100% với các item khác
    listNav.push({
      key: "ai_alert_menu_key",
      icon: <NotificationIcon />,
      label: (
        <span
          style={{
            paddingLeft: 8,
            fontSize: 14,
            paddingRight: 4,
            color: "rgba(0, 0, 0, 1)",
            cursor: "default",
            userSelect: "none",
          }}
        >
          {!collapsed && (t("sideBar.aiAlert") || "Cảnh báo AI")}
        </span>
      ),
    });

    return listNav;
  };

  const listButtonBar = useMemo(
    () => getSideBar(nav, collapsed),
    [nav, collapsed, t]
  );

  const onClick = (e) => {
    if (e.key === "ai_alert_menu_key") {
      setSelectedAlertId(null);
      dispatch(actions.setSelectedAiAlert(null));
      navigate("/ai-alert");
      setCurrent(e.key);
      return;
    }
    setCurrent(e.key);
  };

  const handleOnBreakPoint = (e) => {
    setCollapsed(e);
  };

  return (
    <div className={cx("side-bar")}>
      <Sider
        theme="light"
        style={{ background: "#fff" }}
        collapsed={collapsed}
        width={280}
        collapsedWidth={52}
        onBreakpoint={handleOnBreakPoint}
        breakpoint={"lg"}
      >
        <div className={cx("wrap-menu")}>
          <Menu
            onClick={onClick}
            style={{
              fontFamily: "Averta Std CY",
              fontSize: 14,
              overflowX: "hidden",
            }}
            mode="inline"
            items={listButtonBar}
            inlineCollapsed={collapsed}
            className={cx("sidebar-menu")}
          />

          {!collapsed && (
            <div className={cx("ai-alert-container-list")}>
              {alertList.length === 0 ? (
                <div className={cx("ai-alert-empty")}>Không có cảnh báo</div>
              ) : (
                alertList.slice(0, 5).map((item) => {
                  const isSelected =
                    selectedAlertId === item.id ||
                    dataStore?.selectedAiAlert?.id === item.id;
                  return (
                    <div
                      key={item.id}
                      className={cx(
                        "ai-card",
                        isSelected && "ai-card-active"
                      )}
                      onClick={() => handleSelectAlert(item)}
                    >
                      <div className={cx("ai-card-top")}>
                        <span className={cx("ai-card-time")}>
                          {item.time_display}
                        </span>
                        {!item.seen && <span className={cx("ai-card-dot")}></span>}
                      </div>
                      <div className={cx("ai-card-title")}>{item.title}</div>
                      <div className={cx("ai-card-detail")}>{item.content}</div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
        <div
          style={{ background: "#fff" }}
          onClick={() => setCollapsed(!collapsed)}
          className={cx("collap-button")}
        >
          {collapsed ? <ZoomIcon /> : <SmallZoomIcon color="black" />}
          {collapsed ? "" : t("sideBar.smallMenu")}
        </div>
      </Sider>
    </div>
  );
};
export default Sidebar;